/**
 * Mira on Know Your Competitors: a briefing on what the page's numbers mean
 * and how to improve, then follow-up questions answered from the same data.
 */
import { useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { kyc } from "@/lib/api";
import { Icon } from "@/components/ui/icon";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

type Turn = { role: "user" | "assistant"; content: string; unverified?: string[] };

interface Props {
  /** Changes when the competitor set changes, so the briefing is refreshed */
  dataKey: string;
  className?: string;
  /** Shows a panel button that collapses Mira */
  onCollapse?: () => void;
}

export function KycMira({ dataKey, className, onCollapse }: Props) {
  const [briefing, setBriefing] = useState<Turn | null>(null);
  const [briefingError, setBriefingError] = useState<string | null>(null);
  const [turns, setTurns] = useState<Turn[]>([]);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef<HTMLDivElement>(null);

  const loadBriefing = () => {
    setBriefing(null);
    setBriefingError(null);
    void kyc.mira().then((res) => {
      if ("data" in res && res.data) {
        setBriefing({ role: "assistant", content: res.data.answer, unverified: res.data.unverified });
        setSuggestions(res.data.suggestions);
      } else {
        setBriefingError(res.error?.message ?? "Mira couldn't load the briefing.");
      }
    });
  };

  // The saved conversation (questions and answers persist across reloads)
  useEffect(() => {
    void kyc.miraHistory().then((res) => {
      if ("data" in res && res.data) {
        setTurns(res.data.messages.map((m) => ({ role: m.role, content: m.content, unverified: m.unverified })));
      }
    });
  }, []);

  // The briefing follows the competitor set
  useEffect(() => {
    loadBriefing();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataKey]);

  const clearConversation = async () => {
    const res = await kyc.clearMira();
    if ("data" in res) setTurns([]);
  };

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [turns.length, busy]);

  const ask = async (question: string) => {
    const q = question.trim();
    if (!q || busy) return;
    setInput("");
    setTurns((t) => [...t, { role: "user", content: q }]);
    setBusy(true);
    // Earlier turns are read from the saved thread on the server
    const res = await kyc.mira(q);
    setBusy(false);
    setTurns((t) => [
      ...t,
      "data" in res && res.data
        ? { role: "assistant", content: res.data.answer, unverified: res.data.unverified }
        : { role: "assistant", content: `_${res.error?.message ?? "Mira couldn't answer."}_` },
    ]);
  };

  const asked = new Set(turns.filter((t) => t.role === "user").map((t) => t.content));

  return (
    <section className={cn("flex min-h-0 flex-col", className)}>
      <MiraHeader onCollapse={onCollapse}>
        {turns.length > 0 && (
          <button
            type="button"
            onClick={clearConversation}
            disabled={busy}
            title="Clear the conversation"
            className="flex h-7 items-center gap-1 rounded-md px-1.5 font-mono text-[11px] uppercase tracking-widest text-muted-foreground transition-colors hover:bg-foreground/5 hover:text-foreground disabled:opacity-40"
          >
            <Icon name="delete-02" size={11} />
            Clear
          </button>
        )}
        <button
          type="button"
          onClick={loadBriefing}
          disabled={!briefing && !briefingError}
          title="Refresh the briefing"
          className="flex h-7 items-center gap-1 rounded-md px-1.5 font-mono text-[11px] uppercase tracking-widest text-muted-foreground transition-colors hover:bg-foreground/5 hover:text-foreground disabled:opacity-40"
        >
          <Icon name="refresh" size={11} />
          Refresh
        </button>
      </MiraHeader>

      <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-5 py-4">
        {/* Briefing */}
        {briefingError ? (
          <div className="space-y-2">
            <p className="font-sans text-xs text-muted-foreground">{briefingError}</p>
            <button type="button" onClick={loadBriefing} className="font-mono text-[11px] uppercase tracking-widest underline-offset-2 hover:underline">
              Try again
            </button>
          </div>
        ) : briefing ? (
          <Message turn={briefing} />
        ) : (
          <div className="space-y-3">
            <p className="font-sans text-xs text-muted-foreground">Mira is reading your numbers…</p>
            {[...Array(6)].map((_, i) => <Skeleton key={i} className={cn("h-3", i % 3 === 2 ? "w-2/3" : "w-full")} />)}
          </div>
        )}

        {/* Conversation */}
        {turns.map((t, i) => <Message key={i} turn={t} />)}
        {busy && (
          <div className="flex items-center gap-2 text-muted-foreground">
            <Icon name="refresh" size={11} className="animate-spin" />
            <span className="font-sans text-xs">Thinking…</span>
          </div>
        )}

        {/* Suggested questions */}
        {briefing && !busy && (
          <div className="flex flex-wrap gap-1.5">
            {suggestions.filter((s) => !asked.has(s)).slice(0, 3).map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => ask(s)}
                className="rounded-md border border-border px-2 py-1 text-left font-sans text-[11px] text-muted-foreground transition-colors hover:border-foreground/40 hover:text-foreground"
              >
                {s}
              </button>
            ))}
          </div>
        )}
        <div ref={endRef} />
      </div>

      <form
        onSubmit={(e) => { e.preventDefault(); void ask(input); }}
        className="flex items-center gap-2 border-t border-border px-4 py-3"
      >
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Ask Mira about these numbers…"
          disabled={!briefing || busy}
          className="h-8 flex-1 rounded-md border border-border bg-background px-3 font-sans text-xs outline-none placeholder:text-muted-foreground/60 focus:border-foreground/40 disabled:opacity-50"
        />
        <button
          type="submit"
          disabled={!input.trim() || busy || !briefing}
          aria-label="Ask"
          className="flex h-8 w-8 items-center justify-center rounded-md bg-foreground text-background transition-opacity disabled:opacity-30"
        >
          <Icon name="sent" size={13} />
        </button>
      </form>
    </section>
  );
}

/** Mira's panel header, shared by every state of the panel. */
export function MiraHeader({ onCollapse, children }: { onCollapse?: () => void; children?: React.ReactNode }) {
  return (
    <div className="flex h-12 shrink-0 items-center justify-between border-b border-border px-4">
      <div className="flex items-center gap-2">
        <Icon name="magic-wand-01" size={12} className="text-muted-foreground" />
        <span className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground">Mira</span>
      </div>
      <div className="flex items-center gap-1">
        {children}
        {onCollapse && (
          <button
            type="button"
            onClick={onCollapse}
            title="Hide Mira"
            aria-label="Hide Mira"
            className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-foreground/5 hover:text-foreground"
          >
            <Icon name="panel-right-close" size={15} />
          </button>
        )}
      </div>
    </div>
  );
}

function Message({ turn }: { turn: Turn }) {
  if (turn.role === "user") {
    return (
      <div className="flex justify-end">
        <p className="max-w-[85%] rounded-lg bg-foreground/[0.06] px-3 py-2 font-sans text-xs">{turn.content}</p>
      </div>
    );
  }
  return (
    <div className="font-sans text-xs leading-relaxed">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          h3: ({ children }) => <h3 className="mb-1.5 mt-4 font-mono text-[11px] font-semibold uppercase tracking-widest text-muted-foreground first:mt-0">{children}</h3>,
          p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p>,
          ul: ({ children }) => <ul className="mb-2 list-disc space-y-1 pl-4">{children}</ul>,
          ol: ({ children }) => <ol className="mb-2 list-decimal space-y-1 pl-4">{children}</ol>,
          strong: ({ children }) => <strong className="font-semibold text-foreground">{children}</strong>,
          a: ({ href, children }) => <a href={href} target="_blank" rel="noopener noreferrer" className="underline underline-offset-2">{children}</a>,
        }}
      >
        {turn.content}
      </ReactMarkdown>
      {turn.unverified && turn.unverified.length > 0 && (
        <p className="mt-2 font-mono text-[10px] text-amber-400/80">
          Couldn't verify {turn.unverified.join(", ")} against the data on this page.
        </p>
      )}
    </div>
  );
}
